import { type MessageDescriptor } from 'react-intl';
import { type LocationWithMessageDescriptors, type CamelCaseLocation } from '../types/location';

function isMessageDescriptor(value: unknown): value is MessageDescriptor {
    return (
        typeof value === 'object' && value !== null && 'id' in value && 'defaultMessage' in value
    );
}

/**
 * Serializes location for cookie storage. Replaces react-intl message objects
 * with plain strings using only the `id` field (not defaultMessage).
 */
export function serializeLocationForCookie(
    location: LocationWithMessageDescriptors,
): CamelCaseLocation {
    const result = { ...location };

    if (isMessageDescriptor(result.content)) {
        result.content = result.content.id;
    }
    if (isMessageDescriptor(result.currentPlace)) {
        result.currentPlace = result.currentPlace.id ?? '';
    }

    return result as CamelCaseLocation;
}

/**
 * Deserializes location from cookie. Only when currentPlace === 'Online'
 * (i.e. cookie's current_place), converts string content and currentPlace
 * back to { id, defaultMessage } for react-intl.
 */
export function deserializeLocationFromCookie(
    location: Record<string, string>,
): LocationWithMessageDescriptors {
    if (location.currentPlace?.toLowerCase() !== 'online') {
        return location as unknown as LocationWithMessageDescriptors;
    }

    const result = { ...location } as unknown as LocationWithMessageDescriptors;

    if (typeof result.content === 'string') {
        result.content = {
            id: result.content,
            defaultMessage: result.content,
        };
    }
    if (typeof result.currentPlace === 'string') {
        result.currentPlace = {
            id: result.currentPlace,
            defaultMessage: result.currentPlace,
        };
    }

    return result;
}
